import React, { useState, useEffect } from 'react';
import { UserPlus, Briefcase, MessageSquare, CheckCircle, CheckSquare } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Use native XMLHttpRequest to bypass rrweb-recorder
const nativeFetch = (url, options = {}) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(options.method || 'GET', url, true);
    
    if (options.headers) {
      Object.keys(options.headers).forEach(key => {
        xhr.setRequestHeader(key, options.headers[key]);
      });
    }
    
    xhr.onload = function() {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText);
          resolve({ ok: true, data, status: xhr.status });
        } catch (e) {
          resolve({ ok: true, data: xhr.responseText, status: xhr.status });
        }
      } else {
        reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`));
      }
    };
    
    xhr.onerror = () => reject(new Error('Network error'));
    xhr.send(options.body);
  });
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState([
    { icon: UserPlus, label: 'Total Leads', value: '...', color: 'accent-purple' },
    { icon: CheckSquare, label: 'Open Tasks', value: '...', color: 'accent-teal', onClick: () => navigate('/admin/tasks') },
    { icon: Briefcase, label: 'Active Services', value: '...', color: 'accent-blue' },
    { icon: MessageSquare, label: 'Published Testimonials', value: '...', color: 'accent-green' },
    { icon: CheckCircle, label: 'Active Clients', value: '...', color: 'accent-orange' },
  ]);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      
      // Fetch leads count
      const leadsRes = await nativeFetch(`${BACKEND_URL}/api/leads`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const leadsCount = Array.isArray(leadsRes.data) ? leadsRes.data.length : 0;

      // Fetch services count
      const servicesRes = await nativeFetch(`${BACKEND_URL}/api/services`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const servicesCount = Array.isArray(servicesRes.data) ? servicesRes.data.length : 0;

      // Fetch testimonials count
      const testimonialsRes = await nativeFetch(`${BACKEND_URL}/api/testimonials`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const testimonialsCount = Array.isArray(testimonialsRes.data) ? testimonialsRes.data.filter(t => t.is_active).length : 0;

      // Fetch clients count
      const clientsRes = await nativeFetch(`${BACKEND_URL}/api/clients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const clientsCount = Array.isArray(clientsRes.data) ? clientsRes.data.length : 0;

      // Fetch open tasks count (pending + in_progress + overdue)
      const tasksRes = await nativeFetch(`${BACKEND_URL}/api/admin/tasks`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const openTasksCount = Array.isArray(tasksRes.data) 
        ? tasksRes.data.filter(t => ['pending', 'in_progress', 'overdue'].includes(t.status)).length 
        : 0;

      setStats([
        { icon: UserPlus, label: 'Total Leads', value: leadsCount.toString(), color: 'accent-purple' },
        { icon: CheckSquare, label: 'Open Tasks', value: openTasksCount.toString(), color: 'accent-teal', onClick: () => navigate('/admin/tasks') },
        { icon: Briefcase, label: 'Active Services', value: servicesCount.toString(), color: 'accent-blue' },
        { icon: MessageSquare, label: 'Published Testimonials', value: testimonialsCount.toString(), color: 'accent-green' },
        { icon: CheckCircle, label: 'Active Clients', value: clientsCount.toString(), color: 'accent-orange' },
      ]);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    }
  };
  
  return (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <h1 className="heading-1">Dashboard</h1>
        <p className="body-large">Overview of your visa consultancy CRM system</p>
      </div>

      <div className="stats-grid-admin">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div 
              key={index} 
              className={`stat-card-admin ${stat.color} ${stat.onClick ? 'clickable' : ''}`}
              onClick={stat.onClick}
              style={{ cursor: stat.onClick ? 'pointer' : 'default' }}
            >
              <div className="stat-icon">
                <Icon size={32} />
              </div>
              <div className="stat-info">
                <p className="stat-value heading-hero">{stat.value}</p>
                <p className="stat-label body-small">{stat.label}</p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="dashboard-grid">
        <div className="dashboard-card">
          <h2 className="heading-2">Quick Actions</h2>
          <div className="quick-actions">
            <button 
              className="action-btn btn-secondary"
              onClick={() => navigate('/admin/leads')}
            >
              <UserPlus size={20} />
              <span>View Leads</span>
            </button>
            <button 
              className="action-btn btn-secondary"
              onClick={() => navigate('/admin/services')}
            >
              <Briefcase size={20} />
              <span>Manage Services</span>
            </button>
            <button 
              className="action-btn btn-secondary"
              onClick={() => navigate('/admin/testimonials')}
            >
              <MessageSquare size={20} />
              <span>Add Testimonial</span>
            </button>
          </div>
        </div>

        <div className="dashboard-card">
          <h2 className="heading-2">Recent Activity</h2>
          <p className="body-medium">No recent activity to display</p>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;